// Utils
import { BoardSections, BOARD_TYPE } from '../../boards/boardConstants';
import { COLUMN_TYPE } from '../../columns/columnConstants';
import { ALIAS_TYPE } from '../../alias/aliasConstants';

// Performance improvement - as this is very "hot" code
const getParentBoardAndLocationDetailsForDeepChild = (elements, deepChild) => {
    let element = deepChild;
    let parentId = element.getIn(['location', 'parentId']);
    let parent = elements.get(parentId);
    const visitedAncestors = {};

    if (!parent) {
        return {
            parentBoardId: null,
            isInParentBoardInbox: false,
        };
    }

    while (parent && parent.get('elementType') !== BOARD_TYPE) {
        element = parent;
        parentId = element.getIn(['location', 'parentId']);
        parent = elements.get(parentId);

        if (visitedAncestors[parentId]) {
            console.warn(`Infinite loop in getAllAncestorIds for: ${element?.get('_id')}`);
            break;
        }
        visitedAncestors[parentId] = true;
    }

    const elementLocation = element.getIn(['location', 'section']);

    const isInParentBoardInbox = elementLocation === BoardSections.INBOX;

    return {
        parentBoardId: parentId,
        isInParentBoardInbox,
    };
};

/**
 * This function is designed to perform the minimum amount of iterations over the elements
 * collection as possible in order to satisfy the "elementGraphSelector" functions.
 *
 * Because these functions will be executed every time an element changes, we want them to execute as
 * quickly as possible, thus performing the least amount of element iterations as possible.
 *
 * NOTE: This combines 3 functions (plus a little extra) from the "elementGraphUtils" into 1.
 *  - buildPhysicalIdToVirtualIdMap
 *  - buildEntireElementGraph
 *  - buildBoardVisibleElementGraph
 *
 * See each of those functions for more clarity about what this function achieves.
 */
export const calculateElementGraphSelectorData = (elements) => {
    // TOP DOWN MAPS
    // A map of element ID to its immediate children
    const elementGraph = {};
    // A map of board ID to its visible descendants
    const boardVisibleElementGraph = {};
    // A map of element ID to its accessible parents if it's been linked to by an alias
    const elementIdToVirtualParentIdsMap = {};
    // A map of alias ID to their physical board ID
    const aliasIdToPhysicalIdMap = {};

    // BOTTOM UP MAPS
    // A map of element ID to their actual physical *PARENT* ID
    const parentIdMap = {};
    // A map of element ID to its physical parent *BOARD* ID
    const elementIdToAncestorBoardIdMap = {};

    // HELPERS
    // A map of board IDs to parent alias IDs
    const linkedIdToParentIdsMap = {};
    // Map of boardID to true - if the element is a board
    const physicalBoardIdMap = {};
    // A map of column IDs to their parent board ID
    const columnIdToParentBoardIdMap = {};

    // Slight performance improvement - use a for loop instead of a forEach
    const elementIds = elements.keySeq().toArray();
    const elementCount = elementIds.length;

    // Performance improvement.  First get all the boardIds so we can short circuit more expensive checks
    // First:
    //  - Map all column IDs to their parent board IDs
    //  - Find the board IDs that are linked to by aliases and associate the alias's parent ID to that board
    //  - Keep track of boards that are part of the physical tree, as we don't want to change to an aliases parent ID
    //      in this case
    for (let i = 0; i < elementCount; i++) {
        const elementId = elementIds[i];
        const element = elements.get(elementId);

        if (!element) continue;

        // Don't add elements that are in the trash into the maps
        if (element.getIn(['location', 'section']) === BoardSections.TRASH) continue;

        const parentId = element.getIn(['location', 'parentId']);
        parentIdMap[elementId] = parentId;

        const elementType = element.get('elementType');

        // Attempt performance improvement
        if (elementType === COLUMN_TYPE) {
            // If the parent ID of an element is a column, map that ID to the column's parent which will be a board.
            columnIdToParentBoardIdMap[elementId] = parentId;
        } else if (elementType === ALIAS_TYPE) {
            const linkedBoardId = element.getIn(['content', 'linkTo']);
            linkedIdToParentIdsMap[linkedBoardId] = linkedIdToParentIdsMap[linkedBoardId] || [];
            linkedIdToParentIdsMap[linkedBoardId].push(parentId);

            aliasIdToPhysicalIdMap[elementId] = linkedBoardId;
        } else if (elementType === BOARD_TYPE) {
            physicalBoardIdMap[elementId] = true;
        }
    }

    // Now we can loop through the elements again and using the previously calculated maps, we can quickly
    // find board ancestors, etc.
    for (let i = 0; i < elementCount; i++) {
        const elementId = elementIds[i];
        const element = elements.get(elementId);

        if (!element) continue;

        // Don't add elements that are in the trash into the maps
        if (element.getIn(['location', 'section']) === BoardSections.TRASH) continue;

        const parentId = element.getIn(['location', 'parentId']);

        const mappedAliasParentIds = linkedIdToParentIdsMap[elementId];

        if (mappedAliasParentIds) {
            // If the parent of this board is in the physical tree, then include it in the parents list
            // e.g. If it's a skeleton (so in another user's hierarchy) then ignore it
            if (physicalBoardIdMap[parentId]) mappedAliasParentIds.push(parentId);
            elementIdToVirtualParentIdsMap[elementId] = mappedAliasParentIds;
        }

        // Element Graph
        elementGraph[parentId] = elementGraph[parentId] || [];
        elementGraph[parentId].push(elementId);

        const immediateParentIsBoard = physicalBoardIdMap[parentId];

        let parentBoardId;

        if (immediateParentIsBoard) {
            parentBoardId = parentId;
            // Specifically written like this for performance
        } else {
            const result = getParentBoardAndLocationDetailsForDeepChild(elements, element);
            parentBoardId = result.parentBoardId; // eslint-disable-line prefer-destructuring
        }

        // If the parent elements haven't been retrieved yet, don't add this child to the map
        if (parentBoardId === undefined) continue;

        boardVisibleElementGraph[parentBoardId] = boardVisibleElementGraph[parentBoardId] || [];
        boardVisibleElementGraph[parentBoardId].push(elementId);

        elementIdToAncestorBoardIdMap[elementId] = parentBoardId;
    }

    return {
        elementGraph,
        boardVisibleElementGraph,
        aliasIdToPhysicalIdMap,
        elementIdToVirtualParentIdsMap,
        columnIdToParentBoardIdMap,
        elementIdToAncestorBoardIdMap,
        parentIdMap,
    };
};
